import React, { useState } from "react";
import api, { formatErr } from "../lib/api";
import PhotoUpload from "./PhotoUpload";
import GpsField from "./GpsField";
import { X } from "lucide-react";
import { toast } from "sonner";
import { LEVELS_OF_PREVENTION } from "../lib/constants";

const EMPTY_FORM = {
  visit_date: new Date().toISOString().slice(0, 10),
  observations: "",
  new_health_issues_text: "",
  level_of_prevention: "",
  care_provided: "",
  education_provided: "",
  water_source: "",
  toilet_facility: "",
  waste_disposal: "",
  ventilation: "",
  overall_hygiene: "fair",
  follow_up_required: false,
  follow_up_date: "",
  photo_paths: [],
  latitude: null,
  longitude: null,
  remarks: "",
};

export default function AddRevisitModal({ household, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        new_health_issues: form.new_health_issues_text.split(",").map((s) => s.trim()).filter(Boolean),
        follow_up_date: form.follow_up_date || null,
      };
      delete payload.new_health_issues_text;
      await api.post(`/households/${household.id}/revisits`, payload);
      toast.success("Revisit recorded");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-3xl p-6 my-8" data-testid="revisit-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">Follow-up visit</div>
            <h2 className="font-heading text-2xl font-semibold mt-1">Revisit · {household.head_of_family}</h2>
            <p className="text-xs text-brand-muted mt-1">{household.village}</p>
          </div>
          <button type="button" onClick={onClose} className="h-9 w-9 rounded-md hover:bg-brand-surfaceAlt grid place-items-center"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-4 rounded-md bg-brand-surfaceAlt border border-brand-border p-3 text-sm">
          <div className="text-[10px] uppercase tracking-wider text-brand-muted font-semibold mb-1">Previous context</div>
          <div>Original visit on <b>{household.visit_date}</b> by {household.created_by_name}</div>
          {household.health_issues?.length > 0 && <div className="mt-1">Issues: {household.health_issues.join(", ")}</div>}
          {household.care_provided && <div className="mt-1">Previous care: {household.care_provided}</div>}
          {household.overall_hygiene && <div className="mt-1 capitalize">Previous hygiene: {household.overall_hygiene}</div>}
        </div>

        <FormSection title="Visit Details">
          <Grid>
            <Field label="Visit Date *"><Input type="date" required value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} data-testid="rv-date" /></Field>
            <Field label="Observations">
              <Input value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} placeholder="Patient improving / new symptoms…" data-testid="rv-observations" />
            </Field>
          </Grid>
        </FormSection>

        <FormSection title="Hygiene Observation">
          <Grid>
            <Field label="Water Source"><Input value={form.water_source} onChange={(e) => setForm({ ...form, water_source: e.target.value })} placeholder="Tap / Well / Pond" data-testid="rv-water" /></Field>
            <Field label="Toilet Facility"><Input value={form.toilet_facility} onChange={(e) => setForm({ ...form, toilet_facility: e.target.value })} placeholder="Pucca / Open" data-testid="rv-toilet" /></Field>
            <Field label="Waste Disposal"><Input value={form.waste_disposal} onChange={(e) => setForm({ ...form, waste_disposal: e.target.value })} data-testid="rv-waste" /></Field>
            <Field label="Ventilation"><Input value={form.ventilation} onChange={(e) => setForm({ ...form, ventilation: e.target.value })} data-testid="rv-ventilation" /></Field>
            <Field label="Overall Hygiene">
              <Select value={form.overall_hygiene} onChange={(e) => setForm({ ...form, overall_hygiene: e.target.value })} data-testid="rv-overall">
                <option value="poor">Poor</option>
                <option value="fair">Fair</option>
                <option value="good">Good</option>
                <option value="excellent">Excellent</option>
              </Select>
            </Field>
          </Grid>
        </FormSection>

        <FormSection title="Health Assessment & Care">
          <div className="space-y-3">
            <Field label="New Health Issues (comma separated)">
              <Input value={form.new_health_issues_text} onChange={(e) => setForm({ ...form, new_health_issues_text: e.target.value })} placeholder="Fever, BP elevated" data-testid="rv-issues" />
            </Field>
            <Field label="Level of Prevention">
              <Select value={form.level_of_prevention} onChange={(e) => setForm({ ...form, level_of_prevention: e.target.value })} data-testid="rv-prevention">
                <option value="">Select level…</option>
                {LEVELS_OF_PREVENTION.map((l) => <option key={l} value={l}>{l}</option>)}
              </Select>
            </Field>
            <Field label="Care Provided">
              <Textarea value={form.care_provided} onChange={(e) => setForm({ ...form, care_provided: e.target.value })} placeholder="BP check, wound dressing, referral…" data-testid="rv-care" />
            </Field>
            <Field label="Health Education Provided">
              <Textarea value={form.education_provided} onChange={(e) => setForm({ ...form, education_provided: e.target.value })} placeholder="Diet advice, medication adherence…" data-testid="rv-education" />
            </Field>
          </div>
        </FormSection>

        <FormSection title="Follow-up">
          <Grid>
            <Field label="Follow-up Required">
              <label className="flex items-center gap-2 h-12 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border cursor-pointer">
                <input type="checkbox" checked={form.follow_up_required} onChange={(e) => setForm({ ...form, follow_up_required: e.target.checked })} data-testid="rv-followup" />
                <span className="text-sm">Yes, schedule a next visit</span>
              </label>
            </Field>
            <Field label="Follow-up Date">
              <Input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} disabled={!form.follow_up_required} data-testid="rv-followup-date" />
            </Field>
          </Grid>
        </FormSection>

        <FormSection title="Photos & Location">
          <div className="space-y-3">
            <Field label="Photos (max 3)">
              <PhotoUpload value={form.photo_paths} onChange={(v) => setForm({ ...form, photo_paths: v })} maxCount={3} testId="rv-photos" />
              <p className="text-xs text-brand-muted mt-1">After saving the revisit you can generate an AI field report from these photos.</p>
            </Field>
            <Field label="Geo-Tag">
              <GpsField latitude={form.latitude} longitude={form.longitude} onChange={(lat, lng) => setForm({ ...form, latitude: lat, longitude: lng })} />
            </Field>
            <Field label="Remarks">
              <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="rv-remarks" />
            </Field>
          </div>
        </FormSection>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-11 px-4 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt">Cancel</button>
          <button type="submit" disabled={saving} className="h-11 px-6 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium disabled:opacity-60" data-testid="rv-submit">
            {saving ? "Saving…" : "Save Revisit"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="mt-5 rounded-md border border-brand-border p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-brand-muted font-bold mb-3">{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, children, className = "" }) {
  return <div className={className}><label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">{label}</label><div className="mt-1.5">{children}</div></div>;
}
function Input(props) { return <input {...props} className="w-full h-12 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none disabled:opacity-50" />; }
function Select({ children, ...props }) { return <select {...props} className="w-full h-12 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none">{children}</select>; }
function Textarea(props) { return <textarea {...props} rows={3} className="w-full px-3 py-2 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none resize-y" />; }
